import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ApiResponse } from "@/interfaces/api-response";
import { useClickOutside, useDebounce } from "primereact/hooks";
import { InputText } from "primereact/inputtext";
import { HTMLAttributes } from "react";

type AutoCompleteProps<T extends { _id: string; name: string }> = {
  funcCall?: (params: {
    search: string;
    size: number;
    page: number;
  }) => Promise<ApiResponse<T[]>>;
  value: T | null;
  onChange: (value: T | null) => void;
} & HTMLAttributes<HTMLDivElement>;

function AutoComplete<T extends { _id: string; name: string }>({
  funcCall,
  value,
  onChange,
  ...props
}: AutoCompleteProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, debouncedSearchTerm, setSearchTerm] = useDebounce("", 400);
  const [data, setData] = useState<ApiResponse<T[]>>({
    data: [],
    error: null,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);

  const fieldRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useClickOutside(fieldRef as RefObject<HTMLDivElement>, () => {
    setIsOpen(false);
    setSearchTerm("");
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !funcCall) {
        setData({ data: [], total: 0, error: null });
        return;
      }
      try {
        setLoading(true);
        const result = await funcCall({
          search: debouncedSearchTerm,
          size: 10,
          page: 1,
        });
        setData(result);
      } catch (error) {
        console.error(error);
        setData({ data: [], total: 0, error: "Error fetching data" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearchTerm, isOpen, funcCall]);

  useLayoutEffect(() => {
    if (isOpen && fieldRef.current && dropdownRef.current) {
      const fieldRect = fieldRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.getBoundingClientRect().height;
      const spaceBelow = window.innerHeight - fieldRect.bottom;
      const spaceAbove = fieldRect.top;
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [isOpen, data, loading]);

  const handleSelect = (item: T | null) => {
    onChange(item);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      className="relative"
      ref={fieldRef}
      {...props}
      onClick={(e) => {
        inputRef.current?.focus();
      }}
    >
      {!isOpen ? (
        value ? (
          <div className="flex items-center gap-2 rounded-md border border-gray-300 p-3">
            <i
              className="pi pi-times cursor-pointer text-red-600"
              onClick={() => handleSelect(null)}
            />
            <span className="line-clamp-1">{value.name}</span>
            <i
              className="pi pi-pencil cursor-pointer"
              onClick={() => {
                setIsOpen(true);
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 100);
              }}
            />
          </div>
        ) : (
          <div
            className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-300 p-3"
            onClick={() => {
              setIsOpen(true);
              setTimeout(() => {
                inputRef.current?.focus();
              }, 100);
            }}
          >
            <span>Select an artwork</span>
            <i className="pi pi-plus cursor-pointer" />
          </div>
        )
      ) : (
        <div className="relative w-full">
          <InputText
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            className="w-full"
            ref={inputRef}
          />
          <div className="relative w-full">
            <div
              ref={dropdownRef}
              className="absolute left-0 z-10 max-h-60 w-full overflow-y-auto rounded-md bg-white p-2 px-4 shadow-lg"
              style={{
                top: openUpward ? undefined : "100%",
                bottom: openUpward ? "100%" : undefined,
              }}
            >
              {data.data.map((item) => (
                <div
                  key={item._id}
                  className="mb-2 flex cursor-pointer justify-between rounded-md border border-gray-300 p-2 hover:bg-gray-100"
                  onClick={() => handleSelect(item)}
                >
                  <span>{item.name}</span>
                </div>
              ))}
              {loading && (
                <p className="flex w-full items-center justify-center pb-4">
                  <i className="pi pi-spin pi-spinner"></i>
                </p>
              )}
              {!loading && data.data.length === 0 && (
                <div className="p-2 text-gray-500">No results found</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AutoComplete;
